import { debounce } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AutocompleteOption,
  fetchGlobalAutocomplete,
} from '../services/searchService'

// Phase 2 (search overhaul): the typeahead no longer queries Elastic directly from the browser (no
// more REACT_APP_ELASTIC_API_KEY / 4 ElasticsearchAPIConnectors). It now calls the resactly-service
// `/search/autocomplete` proxy (REACT_APP_SERVICE_URL) which forwards to resactly-dis. This removes
// the browser-exposed Elastic key AND lets the backend capture every search term server-side.
// Ranking (relevance + distance) is done server-side, so the old client-side rerank/dedupe is gone.
// The eater's location is forwarded so results are geo-biased the same way the landing page is.
// Callers still get `{ results, loading }` with the same option shape they rendered before.
interface SearchLocation {
  latitude?: number
  longitude?: number
}

const useElasticGlobalSearch = (
  searchInputValue: string,
  location?: SearchLocation,
): { results: AutocompleteOption[]; loading: boolean } => {
  const [results, setResults] = useState<AutocompleteOption[]>([])
  const [loading, setLoading] = useState(false)
  // Monotonic request id — ignore any callback that isn't the latest in-flight search so a slow
  // early keystroke can't overwrite a faster later one (stale-response race).
  const requestSeqRef = useRef(0)

  const lat = location?.latitude
  const lng = location?.longitude

  const fetchAutocompleteResults = useMemo(
    () =>
      debounce(
        (term: string, onResults: (options: AutocompleteOption[]) => void) => {
          void (async () => {
            try {
              const options = await fetchGlobalAutocomplete({
                q: term,
                lat,
                lng,
              })
              onResults(options)
            } catch {
              onResults([])
            }
          })()
        },
        300,
      ),
    [lat, lng],
  )

  useEffect(() => {
    if (!searchInputValue) {
      setResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    const reqId = ++requestSeqRef.current
    fetchAutocompleteResults(searchInputValue, (options) => {
      if (reqId !== requestSeqRef.current) return // a newer keystroke superseded this one
      setResults(options)
      setLoading(false)
    })
  }, [searchInputValue, fetchAutocompleteResults])

  return { results, loading }
}

export default useElasticGlobalSearch
